'use client';
import React, { useRef } from "react";
import PrimaryButton from '@/components/ui/PrimaryButton/PrimaryButton';
import { LanguageType, LanguageDirection } from '@/lib/types';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import { useAnimatedNavigate } from '@/hooks/useAnimatedNavigation';
import { useLenis } from "../contexts/LenisContext";
import Image from "next/image";
import { debug } from '@/lib/debug';
import { useIsLg } from '@/contexts/BreakpointContext';
gsap.registerPlugin(useGSAP, ScrollTrigger);

interface CTASectionProps {
  language?: LanguageType
  imageSrc: string
  imageAlt: string
  heading: string
  paragraph: string
  buttonText: string
  id: string
  ctaSectionRef?: React.RefObject<HTMLElement | null>
  footerHeight?: number
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
}


const CTASection: React.FC<CTASectionProps> = ({
  language = "english",
  imageSrc,
  imageAlt,
  heading,
  paragraph,
  buttonText,
  id,
  ctaSectionRef,
  // footerHeight,
}) => {
  const { lenis } = useLenis();
  const navigate = useAnimatedNavigate();
  const isLg = useIsLg();
  const imageRef = useRef<HTMLImageElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLParagraphElement>(null);
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // debug.log('cta section rerender');

  // useEffect(() => {
  //   // debug.log('footerHeight change:', footerHeight);
  //   // document.dispatchEvent(new Event('footerHeightChange'));
  //   // ScrollTrigger.refresh();
  // }, [footerHeight]);

  useGSAP(() => {

    const section = ctaSectionRef?.current;
    const imageContainer = imageContainerRef.current;
    const image = imageRef.current;
    const headingEl = headingRef.current;
    const paragraphEl = paragraphRef.current;
    const buttonEl = buttonRef.current;

    if (isLg) {
      if (section && imageContainer) {

        // Set initial states
        gsap.set(section, {
          padding: 0,
        });
        gsap.set(imageContainer, {
          borderRadius: 0
        });

        // Create a single timeline for better performance
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: "bottom bottom",
            end: "bottom center",
            scrub: true,
            // markers: true,
            invalidateOnRefresh: true,
          }
        });

        // Batch animations together
        tl.to(section, {
          padding: isLg ? "0 1.5rem" : "0 0.5rem"
        }).to(imageContainer, {
          borderRadius: isLg ? "0 0 32px 32px" : "0 0 24px 24px"
        }, "<"); // "<" makes this animation start at the same time
      }

    }

    // Image scaling animation on reveal
    if (image) {
      // Set initial scale
      gsap.set(image, {
        scale: isLg ? 1.1 : 1.15,
      });

      // Animate to normal scale when in view
      gsap.to(image, {
        scale: 1,
        duration: 2.5,
        ease: "power4.out",
        scrollTrigger: {
          trigger: imageContainer,
          start: "top center",
          toggleActions: "play none none none",
          // markers: true,
        }
      });
    }

    // Blur mask animation for text elements
    if (headingEl && paragraphEl && buttonEl) {
      const textElements = [headingEl, paragraphEl, buttonEl];

      // Set initial states for blur mask animation
      gsap.set(textElements, {
        autoAlpha: 0,
        filter: 'blur(30px)',
        y: +100,
        force3D: true,
      });

      // Create scroll trigger animation for text reveal
      const textTl = gsap.timeline({
        scrollTrigger: {
          trigger: headingEl,
          start: "bottom bottom",
          toggleActions: "play none none none",
          // markers: true,
        }
      });

      // Animate elements with stagger
      textTl.to(textElements, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        y: 0,
        duration: 1.0,
        stagger: 0.2,
        ease: "power2.out",
        force3D: true,
      });
    }

  }, { dependencies: [isLg], revertOnUpdate: true });
  // }, [isLg, ctaSectionRef]);
  // }, { dependencies: [isLg], revertOnUpdate: true });

  return (
    <div className="w-full z-10 pointer-events-none">
      <section
        ref={ctaSectionRef}
        id={id}
        className="
          px-[0.5rem] lg:px-0
          cta-section
          w-full
          z-10
          pointer-events-auto
        ">
        <div ref={imageContainerRef} className="
          relative
          w-full
          h-fit
          tb:min-h-[53.5rem]
          overflow-hidden
          rounded-b-[24px]
          tb:rounded-b-[32px]
          lg:rounded-b-[32px]
          border-secondary
          border-stroke-soft-200
          border-t-0
          py-[7rem]
          px-[1.5rem]
          lg:px-[3.5rem]
          flex flex-col justify-center items-center
        ">

          {/* Background Image */}
          <Image
            ref={imageRef}
            src={imageSrc}
            alt={imageAlt}
            fill
            sizes="100vw"
            loading="eager"
            className="object-cover -z-10"
            style={{ transformOrigin: 'center center' }}
          />

          <div
            className="
            flex flex-col gap-[2rem] lg:gap-[3.5rem] items-center justify-center 
            max-w-[62.3125rem] container-x-small w-[18.4375rem] lg:w-full
            ">
            <div className="flex flex-col gap-[1.25rem] lg:gap-[1.5rem] items-center justify-center">
              <h2
                ref={headingRef}
                style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
                className={`
                    max-w-full container-x-small
                    text-center
                    text-text-reversed
                    text-88-to-36--neue-june
                `}>
                {heading}
              </h2>

              <p
                ref={paragraphRef}
                style={{
                  direction: LANGUAGE_TO_DIRECTION[language],
                }}
                className={`
                    max-w-full lg:max-w-[37rem]
                    text-center
                    text-text-reversed
                    text-22--alexandria
                `}>
                {paragraph}
              </p>
            </div>
            <div ref={buttonRef} className="w-fit">
              <PrimaryButton 
                type="accent" 
                size={isLg ? 64 : 52} 
                text={buttonText} 
                language={language} 
                onClick={() => {
                  // Check if we're on the signup page
                  if (window.location.pathname === '/signup') {
                    const section = document.getElementById('signup-section');
                    if (section) {
                      // Emit event to hide navbar during smooth scroll
                      window.dispatchEvent(new CustomEvent('lenisScrollStart'));

                      const y = section.getBoundingClientRect().top + window.scrollY;

                      lenis?.scrollTo(y, {
                        offset: 0,
                        duration: 2,
                        easing: (t: number) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
                        immediate: false,
                        lock: true,
                        onComplete: () => {
                          // Re-enable user scroll and navbar
                          window.dispatchEvent(new CustomEvent('lenisScrollEnd'));
                        }
                      });
                    }
                  } else {
                    navigate('/signup');
                  }
                }} 
              />
            </div>
          </div>
        </div>
      </section>

      {/* {isLg && <div className="h-screen -z-10 pointer-events-none" style={{ height: `100vh` }} />} */}
      {/* {isLg && <div className="h-screen -z-10 pointer-events-none" style={{ height: `${footerHeight}px` }} />} */}
    </div>
  )
};

export default CTASection;
